import { useParams, Link, Navigate } from "react-router-dom";
import { Check, X, Clock, MapPin, ArrowLeft, MessageCircle } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { Reveal } from "../components/motion";
import { EnquiryDialog } from "../components/EnquiryDialog";
import { PACKAGES, CONTACT } from "../data/site";

export default function PackageDetail() {
  const { slug } = useParams();
  const pkg = PACKAGES.find((p) => p.slug === slug);
  if (!pkg) return <Navigate to="/packages" replace />;

  return (
    <div data-testid="package-detail-page">
      <PageHero eyebrow={pkg.duration} title={pkg.title} subtitle={pkg.tagline} image={pkg.image} />
      <section className="max-w-[1200px] mx-auto px-5 lg:px-10 py-20">
        <Link to="/packages" className="inline-flex items-center gap-2 text-emerald hover-underline text-sm mb-12" data-testid="back-to-packages"><ArrowLeft className="w-4 h-4" /> All packages</Link>

        <div className="grid lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2 space-y-16">
            <Reveal>
              <div className="flex flex-wrap gap-6 pb-8 border-b border-emerald/10">
                <div className="flex items-center gap-2 text-emerald"><Clock className="w-5 h-5 text-gold" /> {pkg.duration}</div>
                <div className="flex items-center gap-2 text-emerald"><MapPin className="w-5 h-5 text-gold" /> {pkg.highlights.length} highlights</div>
              </div>
              <p className="eyebrow text-gold mt-8 mb-4">Highlights</p>
              <div className="flex flex-wrap gap-3">
                {pkg.highlights.map((h) => <span key={h} className="border border-emerald/15 px-4 py-2 text-sm text-ink/70 rounded-full">{h}</span>)}
              </div>
            </Reveal>

            <Reveal>
              <p className="eyebrow text-gold mb-6">Day-by-Day Itinerary</p>
              <div className="space-y-8">
                {pkg.itinerary.map((it, i) => (
                  <div key={i} className="flex gap-6" data-testid={`itinerary-day-${i}`}>
                    <div className="shrink-0 w-16">
                      <span className="font-serif text-3xl text-gold/50">{String(i + 1).padStart(2, "0")}</span>
                    </div>
                    <div className="border-l border-emerald/15 pl-6 pb-2">
                      <p className="text-xs uppercase tracking-widest text-gold">{it.day}</p>
                      <h3 className="font-serif text-2xl text-emerald mt-1">{it.title}</h3>
                      <p className="text-ink/60 mt-2 leading-relaxed">{it.text}</p>
                    </div>
                  </div>
                ))}
              </div>
            </Reveal>

            <Reveal>
              <div className="grid sm:grid-cols-2 gap-10">
                <div>
                  <p className="eyebrow text-emerald mb-5">What's Included</p>
                  <ul className="space-y-3">
                    {pkg.included.map((x) => <li key={x} className="flex gap-3 text-ink/70 text-sm"><Check className="w-5 h-5 text-emerald shrink-0" /> {x}</li>)}
                  </ul>
                </div>
                <div>
                  <p className="eyebrow text-ink/40 mb-5">Exclusions</p>
                  <ul className="space-y-3">
                    {pkg.excluded.map((x) => <li key={x} className="flex gap-3 text-ink/50 text-sm"><X className="w-5 h-5 text-ink/30 shrink-0" /> {x}</li>)}
                  </ul>
                </div>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-1">
            <div className="sticky top-28 border border-emerald/15 p-8 bg-white">
              <p className="text-ink/40 text-xs uppercase tracking-wider">Package Price</p>
              <p className="font-serif text-3xl text-emerald mt-1">{pkg.price}</p>
              <p className="text-ink/50 text-sm mt-1">per person · customisable</p>
              <EnquiryDialog title={`Book · ${pkg.title}`} presetPackage={pkg.title} presetType="Booking" trigger={
                <button className="mt-6 w-full bg-emerald text-cream py-4 text-xs uppercase tracking-[0.2em] font-semibold hover:bg-gold transition-colors" data-testid="detail-book-btn">Book This Tour</button>
              } />
              <EnquiryDialog title="Get a Free Quote" presetPackage={pkg.title} trigger={
                <button className="mt-3 w-full border border-emerald text-emerald py-4 text-xs uppercase tracking-[0.2em] font-semibold hover:bg-emerald hover:text-cream transition-colors" data-testid="detail-quote-btn">Get Free Quote</button>
              } />
              <a href={`https://wa.me/${CONTACT.whatsapp}?text=${encodeURIComponent("Hi! I'm interested in the " + pkg.title + " package.")}`} target="_blank" rel="noreferrer"
                className="mt-3 w-full flex items-center justify-center gap-2 bg-[#25D366]/10 text-emerald py-4 text-xs uppercase tracking-[0.2em] font-semibold hover:bg-[#25D366] hover:text-white transition-colors" data-testid="detail-whatsapp-btn">
                <MessageCircle className="w-4 h-4" /> WhatsApp
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
